import { useState, useEffect } from 'react';
import { getDatabase, ref, push, set, get } from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { motion, useReducedMotion } from "framer-motion";
import { Sparkles, Database, ArrowRight, Users } from 'lucide-react';
import FeedbackForm from '../components/feedback';
import ListModal from '../components/modalList';
import '../pagesCSS/home.css';
import '../pagesCSS/home.mobile.css';
import tinaImage from '../Assets/Images/developers/tina_chi.png';
import annieImage from '../Assets/Images/developers/annie_kakaveti.jpeg';
import clarissaImage from '../Assets/Images/developers/clarissa_cheung.jpeg';
import Swal from 'sweetalert2';

const Home = () => {
  const [showModal, setShowModal] = useState(false);
  const [feedbackEntry, setFeedbackEntry] = useState(null);
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState('User');
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const auth = getAuth();
  const db = getDatabase();
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };

    if (!shouldReduceMotion) {
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [shouldReduceMotion]);

  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [showModal]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: shouldReduceMotion ? "tween" : "spring",
        damping: 25,
        stiffness: 200
      }
    }
  };

  const developers = [
    {
      name: "Tina Chi",
      role: "Creator, Computer Science @ UF",
      linkedin: "https://www.linkedin.com/in/tinachi1/",
      image: tinaImage,
      gradient: "linear-gradient(135deg, #9a6aec, #9333ea)" 
    },
    {
      name: "Annie Kakaveti",
      role: "Computer Science & Digital Arts Sciences @ UF",
      linkedin: "https://www.linkedin.com/in/ananyakakaveti/",
      image: annieImage,
      gradient: "linear-gradient(135deg, #9a6aec, #9333ea)" 
    },
    {
      name: "Clarissa Cheung",
      role: "Computer Science & Economics @ UF",
      linkedin: "https://www.linkedin.com/in/clarissa-cheung-054035187/",
      image: clarissaImage,
      gradient: "linear-gradient(135deg, #9a6aec, #9333ea)" 
    }
  ];

  const features = [
    {
      icon: <Database className="w-5 h-5" />,
      text: "Browse company interview data including questions, process, compensation, time frame, and more"
    },
    {
      icon: <Users className="w-5 h-5" />,
      text: "Access company-specific questions from real UF students after contributing"
    },
    {
      icon: <Sparkles className="w-5 h-5" />,
      text: "Prepare for interviews with insider knowledge from your peers"
    }
  ];

  const handleUpdateEntry = async (feedbackEntry) => {
    try {
      if (!user) {

        Swal.fire({
          title: "User not authenticate!",
          text: "Please log in.",
          icon: "error"
        });
        return;
      }
      
      const userRef = ref(db, `feedback/${user.uid}`);
      const newRef = push(userRef);
      
      await set(newRef, {
        timestamp: Date.now(),
        ...feedbackEntry
      });
      Swal.fire({
        title: "Feedback successfully submitted!",
        icon: "success"
      });
      setFeedbackEntry(null);
      setShowModal(false);
    } catch (error) {
      Swal.fire({
        title: "Error submitting feedback",
        icon: "error"
      });
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userId = currentUser.uid;
        setUser(currentUser);
        try {
          const userRef = ref(db, `users/${userId}/username`);
          const snapshot = await get(userRef);
          if (snapshot.exists()) {
            setUsername(snapshot.val());
          }
        } catch (error) {
          console.error("Error fetching username:", error);
        }
      } else {
        setUser(null);
        setUsername('User');
      }
    });
    return () => unsubscribe();
  }, [auth, db]);

  return (
    <div className="home-container">
      {showModal && (
        <ListModal show={true} onClose={() => setShowModal(false)}>
          <FeedbackForm
            entry={feedbackEntry}
            onSubmit={handleUpdateEntry}
            onCancel={() => setFeedbackEntry(null)}
          />
        </ListModal>
      )}

      {/* Animated background blobs */}
      <div className="background-blobs">
        <div className="blob blob-1"></div>
        <div className="blob blob-2"></div>
        <div className="blob blob-3"></div>
      </div>

      {/* 3D Floating Shapes */}
      <div className="floating-shapes">
        {/* Sphere 1 */}
        <motion.div
          className="shape-wrapper sphere-wrapper-1"
          style={{
            transform: `translate(${-mousePosition.x * 0.5}px, ${-mousePosition.y * 0.5}px)`
          }}
          animate={{
            y: [0, 40, 0],
            scale: [1, 1.1, 1]
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="sphere-3d sphere-1"></div>
        </motion.div>

        {/* Torus */}
        <motion.div
          className="shape-wrapper torus-wrapper"
          animate={{
            rotateZ: [0, 360],
            y: [0, -20, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            transform: `translate(${mousePosition.x * 0.8}px, ${mousePosition.y * 0.8}px)`
          }}
        >
          <div className="torus-3d"></div>
        </motion.div>

        {/* Pyramid */}
        <motion.div
          className="shape-wrapper pyramid-wrapper"
          animate={{
            rotateY: [0, 360],
            y: [0, -25, 0]
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            transform: `translate(${-mousePosition.x * 0.6}px, ${-mousePosition.y * 0.6}px)`,
          }}
        >
          <div className="pyramid-3d"></div>
        </motion.div>

        {/* Sphere 2 */}
        <motion.div
          className="shape-wrapper sphere-wrapper-2"
          style={{
            transform: `translate(${mousePosition.x * 0.7}px, ${mousePosition.y * 0.7}px)`
          }}
          animate={{
            y: [0, -30, 0],
            scale: [1, 1.15, 1]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div className="sphere-3d sphere-2"></div>
        </motion.div>

        {/* Octahedron */}
        <motion.div
          className="shape-wrapper octahedron-wrapper"
          animate={{
            rotateX: [0, 360],
            rotateY: [0, 360],
            y: [0, -15, 0]
          }}
          transition={{
            duration: 11,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            transform: `translate(${-mousePosition.x * 0.4}px, ${-mousePosition.y * 0.4}px)`,
          }}
        >
          <div className="octahedron-3d"></div>
        </motion.div>
      </div>

      <div className="content-wrapper">
        {/* Hero Section */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="hero-section"
        >
          <motion.h1 variants={itemVariants} className="hero-title">
            <span className="title-normal">Welcome to </span>
            <span className="title-gradient">WiCSEpedia</span>
          </motion.h1>

          <motion.p variants={itemVariants} className="hero-subtitle">
            UF WiCSE's official database. Open to any and all people regardless of gender. Your hub for company interview preparation.
          </motion.p>

          {/* Feature Cards */}
          <motion.div variants={itemVariants} className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                whileHover={{ y: -4, scale: 1.02 }}
                className="feature-card"
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <p className="feature-text">
                  {feature.text}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="cta-buttons">
            <motion.a
              href="#/list"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="btn-primary"
            >
              Get Started
              <ArrowRight className="btn-icon" />
            </motion.a>
            
            <motion.button
              onClick={() => setShowModal(true)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="btn-secondary"
            >
              Submit Feedback
            </motion.button>
          </motion.div>
        </motion.div>

        {/* Developers Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="developers-section"
        >
          <div className="section-header">
            <h2 className="section-title">Meet the Developers</h2>
            <p className="section-subtitle">The team behind WiCSEpedia</p>
          </div>

          <div className="developers-grid">
            {developers.map((dev, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                onClick={() => window.open(dev.linkedin, '_blank')}
                className="developer-card"
              >
                <div className="dev-accent" style={{ background: dev.gradient }}></div>
                <img className="dev-image" src={dev.image} alt={dev.name}/>
                
                <h3 className="dev-name">
                  {dev.name}
                </h3>
                
                <p className="dev-role">
                  {dev.role}
                </p>

                <div className="dev-arrow">
                  <ArrowRight className="arrow-icon" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Home;